import { useMemo, useEffect } from 'react';

import { Dialog } from '@components/dialog/Dialog';
import { Form } from '@components/forms';
import { FormSection } from '@components/forms/FormSection';
import { useGetScreenSize } from '@utils/useGetScreenSize';
import { Document, PackageTypeId } from '@models/document';
import { FormFieldType, TFormField } from '@models/forms/formFields.type';
import { Grid } from '@mui/material';
import Loading from '@components/Loading';
import { useGetPolicy } from '@services/policy/policies';
import { getFormattedPolicyNumber } from '@utils/policy/getFormattedPolicyNumber';
import { useForm } from 'react-hook-form';
import { useCreateDocumentGeneration } from '@services/document/useCreateDocumentGeneration';
import { ToastType } from '@components/toast/ToastType.enum';
import { useSnackbar } from 'notistack';
import { getUndefinedIfEmpty } from '@utils/getUndefinedIfEmpty';

type EditDocumentDialogProps = {
  document: Document | null;
  onClose: () => void;
};

type AdditionalDataCOI = {
  name: string;
  address?: string;
  descriptionOfOperations?: string;
  specialWording?: string;
  type?: string;
};

export const EditDocumentDialog = ({ document, onClose }: EditDocumentDialogProps) => {
  const { policy, isLoading } = useGetPolicy({ policyId: document?.policyId ?? undefined });
  const { enqueueSnackbar } = useSnackbar();

  const { createDocumentGenerationAsync, isPending: isCreateDocumentLoading } = useCreateDocumentGeneration();

  const { isMobileScreen } = useGetScreenSize();

  const methods = useForm({
    mode: 'onChange',
  });

  const formattedPolicyNumber = policy?.policyNumberPrefix
    ? getFormattedPolicyNumber(policy?.policyNumberPrefix, policy?.policyNumber.policyNumber)
    : '...';

  useEffect(() => {
    if (!document) {
      methods.reset({});
    }
  }, [document, methods]);

  const fields = useMemo(() => {
    const additionalData = document?.additionalData as AdditionalDataCOI;

    const formFields: { [fieldName: string]: TFormField } = {
      name: {
        gridWidth: { xs: 12 },
        name: 'name',
        label: 'Name',
        fieldType: FormFieldType.TextField,
        required: false,
        disabled: true,
        defaultValue: additionalData?.name ?? '',
      },
      address: {
        gridWidth: { xs: 12 },
        name: 'address',
        label: 'Address',
        fieldType: FormFieldType.TextField,
        required: false,
        disabled: true,
        defaultValue: additionalData?.address ?? '',
      },
      descriptionOfOperations: {
        gridWidth: { xs: 12 },
        name: 'descriptionOfOperations',
        label: 'Description Of Operations',
        fieldType: FormFieldType.TextField,
        required: false,
        disabled: true,
        defaultValue: additionalData?.descriptionOfOperations ?? '',
        fieldConfig: {
          textField: {
            multiline: true,
          },
        },
      },
      specialWording: {
        gridWidth: { xs: 12 },
        name: 'specialWording',
        label: 'Special Wording',
        fieldType: FormFieldType.TextField,
        disabled: false,
        defaultValue: additionalData?.specialWording ?? '',
        fieldConfig: {
          textField: {
            rows: 4,
            multiline: true,
          },
        },
      },
    };

    methods.reset({
      name: additionalData?.name ?? '',
      address: additionalData?.address ?? '',
      descriptionOfOperations: additionalData?.descriptionOfOperations ?? '',
      specialWording: additionalData?.specialWording ?? '',
    });

    return formFields;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document]);

  const handleExitDialog = () => {
    onClose();
  };

  const handleSubmit = async () => {
    const formValues = methods.getValues();
    const additionalData = document?.additionalData as AdditionalDataCOI;

    const formattedFormValues = {
      name: getUndefinedIfEmpty(formValues.name),
      address: getUndefinedIfEmpty(formValues.address),
      descriptionOfOperations: getUndefinedIfEmpty(formValues.descriptionOfOperations),
      specialWording: getUndefinedIfEmpty(formValues.specialWording),
      type: getUndefinedIfEmpty(additionalData?.type),
    };

    const documentBody = {
      packageTypeId: PackageTypeId.CertificateOfInsurance,
      quoteId: document?.quoteId ?? undefined,
      policyId: document?.policyId ?? undefined,
      quoteItemLifetimeId: document?.quoteItemLifetimeId ?? undefined,
      additionalData: formattedFormValues,
      replacesDocumentId: document?.id,
    };

    const documentGenerationResponse = await createDocumentGenerationAsync(documentBody as any); // TODO: DocumentGenerationPostRequestBody does not yet support all these fields

    if (!documentGenerationResponse.id) {
      return;
    }

    enqueueSnackbar('Updated Certificate will be available shortly', {
      variant: ToastType.Success,
    });

    handleExitDialog();
  };

  const isOpen = !!document;
  const isValid = methods.formState.isValid;
  const isDirty = methods.formState.isDirty;

  return (
    <Dialog
      open={isOpen}
      sx={{ flex: 'none' }}
      title={`Update COI for ${formattedPolicyNumber}`}
      fullScreen={isMobileScreen}
      handleCloseDialog={handleExitDialog}
      primaryBtnText='Update Certificate'
      primaryBtnAction={handleSubmit}
      isPrimaryBtnDisabled={!isValid || !isDirty}
      isPrimaryBtnLoading={isCreateDocumentLoading}
      isSecondaryBtnDisabled={isCreateDocumentLoading}
      secondaryBtnText='Exit'
      secondaryBtnAction={handleExitDialog}
    >
      {isOpen && (
        <Form methods={methods} formId='create-policy-transaction-form'>
          <Grid
            container
            spacing={0}
            sx={{
              gap: 0,
              mb: 3,
              mt: 0,
              padding: '0px 16px 0 16px',
            }}
          >
            {isLoading ? <Loading /> : <FormSection fields={fields} styleConfig={{ spacing: 2 }} />}
          </Grid>
        </Form>
      )}
    </Dialog>
  );
};
